import { useState } from 'react'
import {
  Paper, Stack, Group, Text, TextInput, Button, ActionIcon, Badge, Select,
  NumberInput, Divider, Tooltip, Switch,
} from '@mantine/core'
import {
  IconTrash, IconCheck, IconRefresh, IconArrowsExchange, IconUsers,
} from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { teamService } from '../../services/teamService'

// Convert the backend's effective_role_caps (resolved value per role, null =
// unlimited) into the cap-hash shape the setter expects (0 = unlimited), keyed
// only by roles the story defines. Used to seed a team's first override from the
// values it was inheriting from the group.
function normalizeEffective(effective, roles) {
  const out = {}
  for (const r of roles) out[r.key] = effective[r.key] == null ? 0 : Number(effective[r.key])
  return out
}

/**
 * Facilitator/admin controls for a single team: rename, per-role caps,
 * member management (reset role, reassign to a sibling team, remove), and
 * disband. Reused by the worlds facilitator surface. `siblingTeams` are the
 * other teams in the same group, used as reassignment targets.
 *
 * `team` is the team_detail payload from the API. On any mutation we invalidate
 * the caller-provided `invalidateKey` so the list refreshes.
 */
export function TeamManagementPanel({ team, siblingTeams = [], invalidateKey }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(team.name)
  const current = team.current_story
  const roles = current?.player_roles || []
  // role_caps = this team's own overrides; effective_role_caps = the resolved
  // value per role after the group-default fallback (null = unlimited). When the
  // team isn't overriding we show the inherited (effective) values read-only.
  const caps = current?.role_caps || {}
  const effective = current?.effective_role_caps || {}
  const claims = current?.role_claims || {}

  // Per-team override is OFF by default — group defaults are the primary control.
  // Seed from the backend so a team that already overrides starts expanded.
  const [override, setOverride] = useState(Boolean(current?.has_role_cap_override))

  // Resolve a player's role key to its friendly name for display in the list.
  const roleName = (key) => roles.find((r) => r.key === key)?.name || key

  // Member story-status badge color. Three states from the backend's
  // session_status: not_started (gray, hasn't begun), in_progress (yellow,
  // actively playing), completed (green, done). See teams_controller#session_status.
  const statusColor = (status) => (
    { completed: 'green', in_progress: 'yellow', not_started: 'gray' }[status] || 'gray'
  )

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['team', team.id] })
    if (invalidateKey) queryClient.invalidateQueries({ queryKey: invalidateKey })
  }
  const onError = (error) => notifications.show({
    color: 'red',
    title: 'Action failed',
    message: error.response?.data?.error || 'Please try again.',
  })

  const rename = useMutation({
    mutationFn: () => teamService.renameTeam(team.id, name.trim()),
    onSuccess: () => { refresh(); notifications.show({ color: 'green', message: 'Team renamed' }) },
    onError,
  })

  const disband = useMutation({
    mutationFn: () => teamService.disbandTeam(team.id),
    onSuccess: () => { refresh(); notifications.show({ color: 'green', message: 'Team disbanded' }) },
    onError,
  })

  const removeMember = useMutation({
    mutationFn: (memberId) => teamService.removeMember(team.id, memberId),
    onSuccess: refresh,
    onError,
  })

  const resetRole = useMutation({
    mutationFn: (memberId) => teamService.resetMemberRole(team.id, memberId),
    onSuccess: () => { refresh(); notifications.show({ color: 'green', message: 'Role freed' }) },
    onError,
  })

  const reassign = useMutation({
    mutationFn: ({ memberId, toTeamId }) => teamService.reassignMember(team.id, memberId, toTeamId),
    onSuccess: refresh,
    onError,
  })

  const setCap = useMutation({
    mutationFn: (roleCaps) => teamService.setRoleCaps(team.id, current.team_story_id, roleCaps),
    onSuccess: () => { refresh(); notifications.show({ color: 'green', message: 'Caps updated' }) },
    onError,
  })

  const updateCap = (roleKey, value) => {
    // value: number; 0/empty = unlimited. Seed from the effective (inherited)
    // caps so a first edit while overriding starts from the group values, then
    // merge so other roles keep theirs.
    const base = Object.keys(caps).length ? caps : normalizeEffective(effective, roles)
    const next = { ...base, [roleKey]: value === '' || value == null ? 0 : Number(value) }
    setCap.mutate(next)
  }

  // Turn override ON: do not write yet — the editor reveals seeded from the
  // inherited values; the first cap edit persists the override. Turn OFF: clear
  // this team's override (empty hash) so it falls back to the group defaults.
  const toggleOverride = (on) => {
    setOverride(on)
    if (!on && Object.keys(caps).length) setCap.mutate({})
  }

  return (
    <Paper withBorder p="md" radius="md" style={{ background: 'var(--bw-panel-bg)' }}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-end" wrap="nowrap">
          <TextInput
            label="Team name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            style={{ flex: 1 }}
            data-testid={`team-name-${team.id}`}
          />
          <Button
            variant="light"
            color="cyan"
            leftSection={<IconCheck size={16} />}
            disabled={!name.trim() || name.trim() === team.name}
            loading={rename.isPending}
            onClick={() => rename.mutate()}
          >
            Save
          </Button>
          <Tooltip label="Disband team">
            <ActionIcon
              color="red"
              variant="subtle"
              size="lg"
              loading={disband.isPending}
              onClick={() => {
                if (window.confirm(`Disband "${team.name}"? This removes the team and its members.`)) {
                  disband.mutate()
                }
              }}
              data-testid={`disband-${team.id}`}
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {roles.length > 0 && (
          <>
            <Divider
              label={(
                <Group gap="sm" wrap="nowrap">
                  <span>Role limits</span>
                  <Switch
                    size="xs"
                    checked={override}
                    onChange={(e) => toggleOverride(e.currentTarget.checked)}
                    label="Override for this team"
                    disabled={setCap.isPending}
                    data-testid={`override-toggle-${team.id}`}
                  />
                </Group>
              )}
              labelPosition="left"
            />
            {!override ? (
              // Inheriting group defaults — show resolved values read-only.
              <Group gap="md" wrap="wrap">
                <Text size="xs" c="dimmed" w="100%">
                  Inheriting group defaults. Turn on the override to set limits just for this team.
                </Text>
                {roles.map((role) => {
                  const eff = effective[role.key]
                  const claimed = claims[role.key] || 0
                  return (
                    <Group key={role.key} gap={6} wrap="nowrap" style={{ minWidth: 150 }}>
                      <Text size="sm" fw={600}>{role.name}</Text>
                      <Badge size="xs" variant="light" color="gray" data-testid={`inherited-cap-${team.id}-${role.key}`}>
                        {eff == null ? 'unlimited' : eff}
                      </Badge>
                      <Badge size="xs" variant="light" color={claimed > 0 ? 'cyan' : 'gray'}>
                        {claimed} taken
                      </Badge>
                    </Group>
                  )
                })}
              </Group>
            ) : (
              <>
                <Text size="xs" c="dimmed">
                  How many teammates can hold each role on this team. 1 = unique. 0 = unlimited.
                </Text>
                <Group gap="md" wrap="wrap">
                  {roles.map((role) => {
                    // Seed from this team's override if set, else the inherited value.
                    const raw = role.key in caps ? caps[role.key] : effective[role.key]
                    const cap = raw == null ? 0 : Number(raw)
                    const claimed = claims[role.key] || 0
                    return (
                      <Stack key={role.key} gap={4} style={{ minWidth: 150 }}>
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm" fw={600}>{role.name}</Text>
                          <Badge size="xs" variant="light" color={claimed > 0 ? 'cyan' : 'gray'}>
                            {claimed} taken
                          </Badge>
                        </Group>
                        <NumberInput
                          size="xs"
                          min={0}
                          max={99}
                          value={cap <= 0 ? 0 : cap}
                          onChange={(v) => updateCap(role.key, v)}
                          disabled={setCap.isPending}
                          data-testid={`cap-${team.id}-${role.key}`}
                        />
                      </Stack>
                    )
                  })}
                </Group>
              </>
            )}
          </>
        )}

        <Divider label={<Group gap={4}><IconUsers size={14} /><span>Members ({team.members.length})</span></Group>} labelPosition="left" />
        {team.members.length === 0 && (
          <Text size="sm" c="dimmed">No members yet. Use "Distribute roster" when creating teams.</Text>
        )}
        <Stack gap="xs">
          {team.members.map((m) => (
            <Group key={m.id} justify="space-between" wrap="nowrap">
              <Group gap="sm" wrap="nowrap">
                <Text size="sm">{m.full_name || m.display_name}</Text>
                {m.role && (
                  <Badge size="xs" variant="light" color="grape" data-testid={`member-role-${m.id}`}>
                    {roleName(m.role)}
                  </Badge>
                )}
                <Badge size="xs" variant="light" color={statusColor(m.current_story_status)} data-testid={`member-status-${m.id}`}>
                  {m.current_story_status}
                </Badge>
              </Group>
              <Group gap={4} wrap="nowrap">
                <Tooltip label="Free this member's role so it can be re-picked">
                  <ActionIcon
                    variant="subtle"
                    color="yellow"
                    onClick={() => resetRole.mutate(m.id)}
                    loading={resetRole.isPending}
                    data-testid={`reset-role-${m.id}`}
                  >
                    <IconRefresh size={16} />
                  </ActionIcon>
                </Tooltip>
                {siblingTeams.length > 0 && (
                  <Select
                    size="xs"
                    placeholder="Move to…"
                    w={130}
                    data={siblingTeams.map((t) => ({ value: String(t.id), label: t.name }))}
                    leftSection={<IconArrowsExchange size={14} />}
                    onChange={(toTeamId) => toTeamId && reassign.mutate({ memberId: m.id, toTeamId })}
                    value={null}
                    data-testid={`reassign-${m.id}`}
                  />
                )}
                <Tooltip label="Remove from team">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => removeMember.mutate(m.id)}
                    loading={removeMember.isPending}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
          ))}
        </Stack>
      </Stack>
    </Paper>
  )
}
